import React, { createContext, useContext, useState, useEffect } from 'react';
import { UserProfile, CompanyInfo } from '../types';
import { db } from '../firebase';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import { checkHasOverdueInvoices, fetchUserCompanies, switchUserCompany, updateCompany } from '../services/dbService';
import { toast } from '../components/Toast';

interface AuthWorkspaceContextType {
  currentUser: UserProfile | null;
  setCurrentUser: React.Dispatch<React.SetStateAction<UserProfile | null>>;
  companyInfo: CompanyInfo | null;
  setCompanyInfo: React.Dispatch<React.SetStateAction<CompanyInfo | null>>;
  userCompanies: CompanyInfo[];
  setUserCompanies: React.Dispatch<React.SetStateAction<CompanyInfo[]>>;
  isBlocked: boolean;
  handleSwitchCompany: (targetCompanyId: string) => Promise<void>;
  handleUpdateCompany: (companyData: Partial<CompanyInfo>) => Promise<void>;
}

const AuthWorkspaceContext = createContext<AuthWorkspaceContextType | undefined>(undefined);

export function AuthWorkspaceProvider({
  children,
  currentUser,
  setCurrentUser
}: {
  children: React.ReactNode;
  currentUser: UserProfile | null;
  setCurrentUser: React.Dispatch<React.SetStateAction<UserProfile | null>>;
}) {
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [userCompanies, setUserCompanies] = useState<CompanyInfo[]>([]);
  const [isBlocked, setIsBlocked] = useState(false);

  // Monitor overdue invoices block
  useEffect(() => {
    if (!currentUser?.companyId || currentUser.superadmin) {
      setIsBlocked(false);
      return;
    }

    const invoicesRef = collection(db, 'companies', currentUser.companyId, 'invoices');
    const unsubscribe = onSnapshot(invoicesRef, async () => {
      try {
        const blocked = await checkHasOverdueInvoices(currentUser.companyId);
        setIsBlocked(blocked);
      } catch (err) {
        console.error("Fejl ved tjek af fakturablokering:", err);
      }
    });

    return () => unsubscribe();
  }, [currentUser?.companyId, currentUser?.superadmin]);

  // Load user companies
  useEffect(() => {
    if (!currentUser) {
      setUserCompanies([]);
      setCompanyInfo(null);
      return;
    }

    const loadCompanies = async () => {
      try {
        const companies = await fetchUserCompanies(currentUser);
        setUserCompanies(companies);
        const current = companies.find(c => c.id === currentUser.companyId);
        if (current) {
          setCompanyInfo(current);
        }
      } catch (err) {
        console.error("Fejl ved hentning af virksomheder i WorkspaceContext:", err);
      }
    };

    loadCompanies();
  }, [currentUser?.uid, currentUser?.companyId, currentUser?.email]);

  // Real-time listener for current company document changes (e.g. branding, CVR, onboarding, subscriptions)
  useEffect(() => {
    if (!currentUser?.companyId) {
      return;
    }

    const companyDocRef = doc(db, 'companies', currentUser.companyId);
    const unsubscribe = onSnapshot(companyDocRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = { id: snapshot.id, ...snapshot.data() } as CompanyInfo;
        setCompanyInfo(data);
        setUserCompanies(prev => {
          const idx = prev.findIndex(c => c.id === data.id);
          if (idx >= 0) {
            const updated = [...prev];
            updated[idx] = data;
            return updated;
          }
          return prev;
        });
      }
    }, (err) => {
      console.warn("Fejl ved realtidsovervågning af virksomhed i AuthWorkspaceContext:", err);
    });

    return () => unsubscribe();
  }, [currentUser?.companyId]);

  // Google Drive background keep-alive removed to prevent unsolicited account popups

  const handleSwitchCompany = async (targetCompanyId: string) => {
    if (!currentUser) return;
    try {
      await switchUserCompany(currentUser.uid, targetCompanyId);
      setCurrentUser(prev => prev ? { ...prev, companyId: targetCompanyId } : null);
      const target = userCompanies.find(c => c.id === targetCompanyId);
      if (target) setCompanyInfo(target);
      const targetName = target?.name || targetCompanyId;
      toast.success(`Du er skiftet til ${targetName}`);
    } catch (err) {
      console.error("Fejl ved skift af virksomhed:", err);
      throw err;
    }
  };

  const handleUpdateCompany = async (companyData: Partial<CompanyInfo>) => {
    if (!companyInfo?.id) return;
    try {
      await updateCompany(companyInfo.id, companyData);
      setCompanyInfo(prev => prev ? { ...prev, ...companyData } : null);
      setUserCompanies(prev => prev.map(c => c.id === companyInfo.id ? { ...c, ...companyData } : c));
    } catch (err) {
      console.error("Fejl ved opdatering af virksomhed:", err);
      throw err;
    }
  };

  return (
    <AuthWorkspaceContext.Provider
      value={{
        currentUser,
        setCurrentUser,
        companyInfo,
        setCompanyInfo,
        userCompanies,
        setUserCompanies,
        isBlocked,
        handleSwitchCompany,
        handleUpdateCompany
      }}
    >
      {children}
    </AuthWorkspaceContext.Provider>
  );
}

export function useAuthWorkspace() {
  const context = useContext(AuthWorkspaceContext);
  if (!context) {
    throw new Error('useAuthWorkspace must be used within an AuthWorkspaceProvider');
  }
  return context;
}
